/**
 * ApprovalCenterPageV2 — proper v2 redesign (G107).
 *
 * Applies the canonical Agentlets v2 inventory pattern from
 * AgentsInventoryPageV2.jsx:
 *
 *   ┌────────────────────────────────────────────────────────────────┐
 *   │  Header: title · subtitle · actions                            │
 *   ├────────────────────────────────────────────────────────────────┤
 *   │  Active filter chips strip                            [× ×]    │
 *   ├──────────────┬─────────────────────────────────────────────────┤
 *   │              │                                                 │
 *   │  FacetRail   │   v2 Table — row-click → Slideout (right)       │
 *   │  (240px)     │   (1fr)                                         │
 *   │              │                                                 │
 *   └──────────────┴─────────────────────────────────────────────────┘
 *
 * Reuses every backend code path from v1 ApprovalCenterPage:
 *   - usePlatform() for approvalInbox / approvalSubmitted / approvalAll /
 *     approvalStats / approvalPolicies and their loaders
 *   - api.approveApprovalRequest, api.rejectApprovalRequest,
 *     api.cancelApprovalRequest for actions
 *   - Same role-aware visibility (isTenantAdmin gates the All / Policies
 *     scopes; non-admins see only Inbox + Submitted)
 *
 * What's new (v2 reimagining, not whitewashing):
 *   - FacetRail replaces top-of-page filter dropdowns + tab pill bar
 *   - Single normalized record list (no per-tab tables) — scope is just
 *     another facet (My Inbox / Submitted / All)
 *   - Slideout for triage: Summary · Decision history · Audit · Policy ·
 *     Comments tabs replace the v1 stacked detail panel
 *   - Bulk approve/reject toolbar appears when rows are checkbox-selected
 *   - Density toggle persisted to localStorage (same UX as inventory)
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isRebirthApprovalsEnabled } from '../utils/featureFlags';
import * as api from '../services/api';
import {
  RefreshCw, Inbox, Send, Activity, FileText, AlertTriangle, Check, X,
  CheckCircle, XCircle, Clock, ChevronRight,
} from 'lucide-react';
import {
  Button, Pill, Card, Modal, Slideout, Table, FacetRail, EmptyState,
  Skeleton, MenuDropdown,
  // RC-103a — V2 form primitives for dynamic formSchema rendering.
  // Per memory `feedback_v2_form_primitives_mandatory.md`: raw HTML
  // <input>/<select>/<textarea> in V2 surfaces is a bug. Use these.
  Input, Textarea, Select, FieldGroup,
} from '../components/v2';
import { Pagination, usePaginationState } from '../components/common/Pagination';

import ApprovalCenterPage from './ApprovalCenterPage';

/* ── Domain maps ─────────────────────────────────────────────────────── */

const STATUS_OPTIONS = [
  { key: 'pending',          label: 'Pending',           tone: 'warning' },
  { key: 'awaiting_second',  label: 'Awaiting 2nd',      tone: 'warning' },
  { key: 'approved',         label: 'Approved',          tone: 'success' },
  { key: 'rejected',         label: 'Rejected',          tone: 'danger' },
  { key: 'escalated',        label: 'Escalated',         tone: 'warning' },
  { key: 'expired',          label: 'Expired',           tone: 'muted' },
  { key: 'cancelled',        label: 'Cancelled',         tone: 'muted' },
];

const URGENCY_OPTIONS = [
  { key: 'critical', label: 'Critical', tone: 'danger' },
  { key: 'high',     label: 'High',     tone: 'warning' },
  { key: 'normal',   label: 'Normal',   tone: 'cyan' },
  { key: 'low',      label: 'Low',      tone: 'muted' },
];

const TYPE_OPTIONS = [
  { key: 'skill_execution', label: 'Skill execution' },
  { key: 'workflow_step',   label: 'Workflow step' },
  { key: 'agent_action',    label: 'Agent action' },
];

const SCOPE_OPTIONS = [
  { key: 'inbox',     label: 'My Inbox' },
  { key: 'submitted', label: 'Submitted' },
  // PROVE7-040 — role-holder oversight of their role's queue (all statuses). Visible to everyone
  // (empty for users with no role queue); it is NOT admin-gated, unlike 'all'.
  { key: 'myrole',    label: 'My Role Queue' },
  { key: 'all',       label: 'All Requests (admin)' },
];

const STATUS_TONE = Object.fromEntries(STATUS_OPTIONS.map(o => [o.key, o.tone]));
const URGENCY_TONE = Object.fromEntries(URGENCY_OPTIONS.map(o => [o.key, o.tone]));

const FACET_GROUPS = (canSeeAll) => ([
  ...(canSeeAll ? [{
    key: 'scope', label: 'Scope',
    options: SCOPE_OPTIONS,
  }] : [{
    key: 'scope', label: 'Scope',
    options: SCOPE_OPTIONS.filter(s => s.key !== 'all'),
  }]),
  { key: 'status',   label: 'Status',   options: STATUS_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
  { key: 'urgency',  label: 'Urgency',  options: URGENCY_OPTIONS.map(o => ({ key: o.key, label: o.label })) },
  { key: 'type',     label: 'Type',     options: TYPE_OPTIONS },
]);

/* ── Helpers ─────────────────────────────────────────────────────────── */

function safeDate(s) {
  if (!s) return null;
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return null;
    return d;
  } catch { return null; }
}

function fmtRelative(s) {
  const d = safeDate(s);
  if (!d) return '—';
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1)   return 'just now';
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function urgencyDot(urgency) {
  const tone = URGENCY_TONE[urgency] || 'muted';
  const cls = tone === 'danger'  ? 'bg-v2-danger'
           : tone === 'warning' ? 'bg-v2-warning'
           : tone === 'cyan'    ? 'bg-v2-cyan'
           : 'bg-v2-ink-muted';
  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`} aria-label={urgency || 'unknown'} />;
}

/* ── Inner component ─────────────────────────────────────────────────── */

function ApprovalCenterPageV2Inner() {
  const navigate = useNavigate();
  const { session, hasPermission } = useAuth();
  const { addToast } = useToast();
  const platform = usePlatform();
  const orgId    = session?.orgId;
  const tenantId = session?.tenantId;
  /* Permission gate — only Tenant Admin and above see "All Requests"
   * scope and the Policies sidebar facet (mirrors v1 isTenantAdmin). */
  const isTenantAdmin = hasPermission && (hasPermission('admin.team') || hasPermission('approvals.viewAll'));

  /* Pull the data + loaders from PlatformContext — same hooks v1 uses. */
  const {
    approvalInbox = [], approvalSubmitted = [], approvalAll = [], approvalMyRole = [],
    approvalInboxCount = 0,
    loadApprovalInbox, loadApprovalInboxCount,
    loadApprovalSubmitted, loadApprovalMyRole, loadApprovalAll, loadApprovalStats,
  } = platform;

  /* ── Normalize: stamp each row with its scope so the FacetRail can
   * filter across them in one Table. v1 had three separate tables. */
  const allRows = useMemo(() => {
    /* A request can belong to MORE THAN ONE scope — e.g. it is in the viewer's
     * personal inbox AND in a role queue they hold. De-dupe by id into one row,
     * but accumulate EVERY scope it belongs to in `_scopes`, so per-scope counts
     * and filters are accurate. (Was: first-scope-wins, which stamped such a row
     * 'inbox' and undercounted the role queue.) */
    const sources = [
      ['inbox', approvalInbox], ['myrole', approvalMyRole],
      ['submitted', approvalSubmitted], ['all', approvalAll],
    ];
    const byId = new Map();
    for (const [scope, list] of sources) {
      for (const r of (list || [])) {
        if (!r?.id) continue;
        const existing = byId.get(r.id);
        if (existing) existing._scopes.add(scope);
        else byId.set(r.id, { ...r, _scopes: new Set([scope]) });
      }
    }
    return Array.from(byId.values());
  }, [approvalInbox, approvalSubmitted, approvalAll, approvalMyRole]);

  /* ── Facet state ─────────────────────────────────────────────────── */
  const [facets, setFacets] = useState(() => ({
    scope:    new Set(['inbox']),  // default: My Inbox
    status:   new Set(),
    urgency:  new Set(),
    type:     new Set(),
  }));

  const toggleFacet = (groupKey, optionKey, nextChecked) => {
    setFacets((prev) => {
      const next = new Set(prev[groupKey] || []);
      if (nextChecked) next.add(optionKey); else next.delete(optionKey);
      return { ...prev, [groupKey]: next };
    });
  };

  const clearFacets = () => setFacets({
    scope: new Set(['inbox']), status: new Set(), urgency: new Set(), type: new Set(),
  });

  /* G231 — Backend ApprovalRequest entity exposes `requestType` (lowercase
   * underscored: skill_execution / workflow_step / agent_action / phase_advance),
   * not `type`. Earlier code read r.type and every TYPE facet count was 0,
   * every row's TYPE column rendered "unknown". Use a canonical accessor
   * so the facet filter, table cell, slideout pills, and detail dl all agree. */
  const typeOf = (r) => r?.requestType || r?.type || '';

  /* valueFor: single-valued facet accessor. Scope is multi-valued (`_scopes`),
   * so it is handled by the membership helpers below, never through valueFor. */
  const valueFor = (r, key) => (key === 'type' ? typeOf(r) : r?.[key]);
  const matchesFacetSet = (r, key, sel) => {
    if (key === 'scope') { for (const sc of sel) if (r?._scopes?.has(sc)) return true; return false; }
    return sel.has(valueFor(r, key));
  };
  const rowHasFacetValue = (r, key, optKey) =>
    key === 'scope' ? !!(r?._scopes?.has(optKey)) : valueFor(r, key) === optKey;

  /* ── Filter pipeline ─────────────────────────────────────────────── */
  const filtered = useMemo(() => {
    return allRows.filter((r) => {
      if (facets.scope.size > 0 && !matchesFacetSet(r, 'scope', facets.scope)) return false;
      if (facets.status.size > 0 && !facets.status.has(r.status)) return false;
      if (facets.urgency.size > 0 && !facets.urgency.has(r.urgency)) return false;
      if (facets.type.size > 0 && !facets.type.has(typeOf(r))) return false;
      return true;
    });
  }, [allRows, facets]);

  /* G175 — Pagination over the filtered list. Reset to page 1 whenever the
   * filtered length changes so the user doesn't land on an empty page. */
  const pg = usePaginationState(10);
  const visibleRows = pg.paginate(filtered);
  useEffect(() => { pg.setPage(0); }, [filtered.length]);

  /* Counts for FacetRail (respect already-applied facets within each
   * group except the group being counted, mirroring AgentsInventoryPageV2). */
  /* G231 — Same field-name fix in the count loop: type → requestType. */

  const facetCounts = useMemo(() => {
    const groups = FACET_GROUPS(isTenantAdmin);
    const counts = {};
    for (const g of groups) {
      counts[g.key] = {};
      for (const opt of g.options) {
        counts[g.key][opt.key] = allRows.filter((r) => {
          for (const og of groups) {
            if (og.key === g.key) continue;
            const sel = facets[og.key];
            if (!sel || sel.size === 0) continue;
            if (!matchesFacetSet(r, og.key, sel)) return false;
          }
          return rowHasFacetValue(r, g.key, opt.key);
        }).length;
      }
    }
    return counts;
  }, [allRows, facets, isTenantAdmin]); // eslint-disable-line

  const facetGroupsWithCounts = useMemo(() => (
    FACET_GROUPS(isTenantAdmin).map(g => ({
      ...g,
      options: g.options.map(o => ({
        ...o,
        count: facetCounts[g.key]?.[o.key] || 0,
        checked: facets[g.key]?.has(o.key) || false,
      })),
    }))
  ), [facetCounts, facets, isTenantAdmin]);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    for (const g of FACET_GROUPS(isTenantAdmin)) {
      for (const optKey of facets[g.key] || []) {
        const opt = g.options.find(o => o.key === optKey);
        chips.push({
          groupKey: g.key, optionKey: optKey,
          label: `${g.label}: ${opt?.label || optKey}`,
        });
      }
    }
    return chips;
  }, [facets, isTenantAdmin]);

  /* ── Density toggle (persisted) ──────────────────────────────────── */
  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('approvals-density') || 'comfortable'; }
    catch { return 'comfortable'; }
  });
  const cycleDensity = () => {
    const next = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(next);
    try { localStorage.setItem('approvals-density', next); } catch {}
  };

  /* ── Slideout ────────────────────────────────────────────────────── */
  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('summary');
  /* RC-103a — Form-input state for form_task / human_task with formSchema.
   *
   * Reset to {} on every selection change so we don't leak field values
   * across rows. Form validation gates the Approve button (required
   * fields must be filled) before we'll POST formData to the BE.
   *
   * SAFETY: When the selected request has no formSchema (or empty
   * fields array), formData stays {} and is passed to handleApprove as
   * undefined — the existing approve-without-form path is unchanged.
   * That's the key zero-regression contract. */
  const [formData, setFormData] = useState({});
  const [formError, setFormError] = useState(null);
  const openSlideout = (row) => {
    setSelected(row);
    setSlideoutTab('summary');
    setFormData({});       // RC-103a — fresh form on every row open
    setFormError(null);
  };
  const closeSlideout = () => {
    setSelected(null);
    setFormData({});       // RC-103a — never persist form values past close
    setFormError(null);
  };

  /* RC-103a — Normalize the formSchema (JSON string or object) into a
   * { fields: [...] } object the renderer expects. Mirrors the parser
   * in HumanTaskInspector.jsx so the author + consumer agree on shape.
   * Returns { fields: [] } when no schema / parse error — caller checks
   * fields.length > 0 to decide whether to render the form block. */
  const parseFormSchema = (raw) => {
    if (raw == null || raw === '') return { fields: [] };
    if (typeof raw === 'object') {
      return { fields: Array.isArray(raw.fields) ? raw.fields : [] };
    }
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return { fields: Array.isArray(parsed?.fields) ? parsed.fields : [] };
      } catch {
        return { fields: [] };
      }
    }
    return { fields: [] };
  };
  const selectedFormSchema = useMemo(
    () => parseFormSchema(selected?.formSchema),
    [selected?.formSchema],
  );
  const hasFormSchema = selectedFormSchema.fields.length > 0;

  /* RC-121 — Conditional show-if evaluator.
   *
   * Supports a small, declarative DSL on field.showIf so author-time
   * forms can hide fields based on values entered in OTHER fields.
   * The DSL is deliberately narrow (single field + one comparator) so
   * the BE can mirror it later without us painting ourselves into an
   * expression-language corner; if real workflows need conjunctions,
   * the author splits them into nested fields with chained showIf.
   *
   * Shape:
   *   field.showIf = { field: 'severity', equals: 'high' }
   *   field.showIf = { field: 'severity', notEquals: 'low' }
   *   field.showIf = { field: 'severity', in: ['high', 'critical'] }
   *   field.showIf = { field: 'severity', notIn: ['info'] }
   *   field.showIf = { field: 'autoApprove', truthy: true }
   *
   * Returns true when the field should render. Missing / malformed
   * showIf returns true so legacy forms (no showIf at all) render
   * exactly as before. The hidden-when-false fields skip BOTH render
   * AND required-check — operators can't be blocked on a field they
   * can't see.
   */
  const evaluateShowIf = useCallback((field, currentFormData) => {
    if (!field || !field.showIf || typeof field.showIf !== 'object') return true;
    const cond = field.showIf;
    const target = cond.field;
    if (!target) return true; // malformed condition → don't hide
    const actual = currentFormData ? currentFormData[target] : undefined;
    if (Object.prototype.hasOwnProperty.call(cond, 'equals')) {
      return actual === cond.equals;
    }
    if (Object.prototype.hasOwnProperty.call(cond, 'notEquals')) {
      return actual !== cond.notEquals;
    }
    if (Array.isArray(cond.in)) {
      return cond.in.includes(actual);
    }
    if (Array.isArray(cond.notIn)) {
      return !cond.notIn.includes(actual);
    }
    if (Object.prototype.hasOwnProperty.call(cond, 'truthy')) {
      // truthy:true → render when actual is truthy; truthy:false → render when falsy
      return cond.truthy === true ? !!actual : !actual;
    }
    // No recognised comparator → render (don't accidentally hide fields
    // because the author wrote `showIf: {}`).
    return true;
  }, []);

  /* RC-103a + RC-121 — Pre-flight validation.
   *
   * Phase 1 (RC-103a) checked only `required` and skipped booleans.
   * RC-121 extends to:
   *   • Multi-select required-check treats an empty array as missing.
   *   • Skips fields hidden by RC-121 showIf — operators can't be
   *     blocked on fields they can't see.
   *   • String fields honour `pattern` (regex), `minLength`, `maxLength`.
   *   • Number fields honour `min`, `max`.
   *   • Each rule is opt-in (missing field property = no check), so
   *     legacy schemas with only `required` continue to behave
   *     identically.
   *
   * Returns true if OK, sets formError otherwise. Stops at the first
   * failure so the operator sees one actionable message at a time.
   */
  const validateFormData = useCallback(() => {
    if (!hasFormSchema) return true;
    // Visible fields only — hidden-by-showIf rows skip validation too.
    const visibleFields = selectedFormSchema.fields.filter(
      (f) => f && evaluateShowIf(f, formData),
    );

    // Required check — empty array (multi-select) counts as missing.
    const missing = visibleFields
      .filter((f) => f.required && (f.type || '').toLowerCase() !== 'boolean')
      .filter((f) => {
        const v = formData[f.name];
        if (Array.isArray(v)) return v.length === 0;
        return v == null || v === '' || (typeof v === 'string' && v.trim() === '');
      })
      .map((f) => f.label || f.name);
    if (missing.length > 0) {
      setFormError(`Please complete: ${missing.join(', ')}`);
      return false;
    }

    // Per-field format / range checks — only run on populated fields
    // (an empty optional field is fine; required-check above already
    // caught empty required fields).
    for (const f of visibleFields) {
      const v = formData[f.name];
      if (v == null || v === '') continue;
      const label = f.label || f.name;

      if (typeof v === 'string') {
        if (f.minLength != null && v.length < Number(f.minLength)) {
          setFormError(`${label} must be at least ${f.minLength} character(s).`);
          return false;
        }
        if (f.maxLength != null && v.length > Number(f.maxLength)) {
          setFormError(`${label} must be at most ${f.maxLength} character(s).`);
          return false;
        }
        if (f.pattern) {
          try {
            const re = new RegExp(f.pattern);
            if (!re.test(v)) {
              setFormError(
                f.patternMessage
                  ? `${label}: ${f.patternMessage}`
                  : `${label} doesn't match the required format.`,
              );
              return false;
            }
          } catch {
            // Malformed regex in schema — skip rather than blocking the
            // operator forever. The author would notice in dev.
          }
        }
      }

      if (typeof v === 'number') {
        if (f.min != null && v < Number(f.min)) {
          setFormError(`${label} must be at least ${f.min}.`);
          return false;
        }
        if (f.max != null && v > Number(f.max)) {
          setFormError(`${label} must be at most ${f.max}.`);
          return false;
        }
      }
    }

    setFormError(null);
    return true;
  }, [hasFormSchema, selectedFormSchema, formData, evaluateShowIf]);

  /* ── Bulk selection ──────────────────────────────────────────────── */
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelected = () => setSelectedIds(new Set());

  /* ── Approve / reject / cancel — same backend code paths as v1 ───── */
  const reload = useCallback(() => {
    if (typeof loadApprovalInbox === 'function')      loadApprovalInbox();
    if (typeof loadApprovalInboxCount === 'function') loadApprovalInboxCount();
    if (typeof loadApprovalSubmitted === 'function')  loadApprovalSubmitted();
    if (typeof loadApprovalMyRole === 'function')     loadApprovalMyRole();
    if (typeof loadApprovalStats === 'function')      loadApprovalStats({ orgId, tenantId });
    if (typeof loadApprovalAll === 'function' && isTenantAdmin) loadApprovalAll({ orgId, tenantId });
  }, [loadApprovalInbox, loadApprovalInboxCount, loadApprovalSubmitted, loadApprovalMyRole, loadApprovalStats, loadApprovalAll, orgId, tenantId, isTenantAdmin]);

  useEffect(() => { reload(); }, [reload]);

  /* Phase 9 — promotion-type approvals MUST route through
   * /api/v1/promotions/{id}/approve | reject because that's where the
   * clone runs in the same transaction as the row flip. The generic
   * /approvals/{id}/approve path was patched to 400 with a redirect
   * message (G562 defense-in-depth) so calling it on a promotion-type
   * row would error out cleanly anyway — but we want the inbox UX to
   * Just Work, so detect the type here and dispatch to the right
   * endpoint up front.
   *
   * The promotion event id is embedded in row.contextData.promotionEventId
   * by PromotionService.buildApprovalRequest at initiate time. */
  const isPromotionRow = (row) => row && (
    (row.requestType || '').toLowerCase() === 'promotion'
    || !!(row.contextData && row.contextData.promotionEventId)
  );

  const promotionEventIdOf = (row) =>
    row && row.contextData ? row.contextData.promotionEventId : null;

  /* PROVE7-019 — intent-template reviews route to the intent approve/reject
   * endpoint (SoD + required-Rego enforced there), mirroring promotion. The
   * version id is embedded in row.contextData.intentVersionId at submit time. */
  const isIntentReviewRow = (row) => row && (
    (row.requestType || '').toLowerCase() === 'intent_template_review'
    || !!(row.contextData && row.contextData.intentVersionId)
  );
  const intentVersionIdOf = (row) =>
    row && row.contextData ? row.contextData.intentVersionId : null;

  const findRow = (id) => allRows.find(r => r.id === id);

  const handleApprove = async (id, comment = '', formData = undefined) => {
    const row = findRow(id);
    if (isPromotionRow(row)) {
      const promoId = promotionEventIdOf(row);
      if (!promoId) {
        addToast({ tone: 'danger', title: 'Approve failed',
          description: 'Promotion event id missing from approval context_data' });
        return;
      }
      if (typeof api.approvePromotion !== 'function') {
        addToast({ tone: 'danger', title: 'Approve failed', description: 'Promotion API not available' });
        return;
      }
      const res = await api.approvePromotion(promoId, { notes: comment });
      if (res?.ok !== false) {
        const payload = res && Object.prototype.hasOwnProperty.call(res, 'data') ? res.data : res;
        addToast({ tone: 'success', title: 'Promotion approved',
          description: payload?.status === 'PROMOTED'
            ? 'Clone created at target instance.'
            : payload?.status === 'FAILED'
              ? 'Approval recorded but clone failed — see promotion history.'
              : undefined });
        closeSlideout();
        reload();
      } else {
        addToast({ tone: 'danger', title: 'Approve failed', description: res.message || res.error || 'Unknown error' });
      }
      return;
    }
    if (isIntentReviewRow(row)) {
      const versionId = intentVersionIdOf(row);
      if (!versionId) {
        addToast({ tone: 'danger', title: 'Approve failed',
          description: 'Intent version id missing from approval context_data' });
        return;
      }
      if (typeof api.approveIntentTemplateVersion !== 'function') {
        addToast({ tone: 'danger', title: 'Approve failed', description: 'Intent API not available' });
        return;
      }
      const res = await api.approveIntentTemplateVersion(versionId);
      if (res?.ok !== false) {
        addToast({ tone: 'success', title: 'Intent template approved' });
        closeSlideout();
        reload();
      } else {
        addToast({ tone: 'danger', title: 'Approve failed', description: res.message || res.error || 'Unknown error' });
      }
      return;
    }
    /* Non-promotion: existing generic path. */
    const fn = api.approveApprovalRequest;
    if (typeof fn !== 'function') {
      addToast({ tone: 'danger', title: 'Approve failed', description: 'API not available' });
      return;
    }
    const res = await fn(id, comment, formData);
    if (res?.ok) {
      addToast({ tone: 'success', title: 'Request approved' });
      closeSlideout();
      reload();
    } else {
      addToast({ tone: 'danger', title: 'Approve failed', description: res?.error || 'Unknown error' });
    }
  };

  const handleReject = async (id, comment = '') => {
    const row = findRow(id);
    if (isPromotionRow(row)) {
      const promoId = promotionEventIdOf(row);
      if (!promoId) {
        addToast({ tone: 'danger', title: 'Reject failed',
          description: 'Promotion event id missing from approval context_data' });
        return;
      }
      if (typeof api.rejectPromotion !== 'function') {
        addToast({ tone: 'danger', title: 'Reject failed', description: 'Promotion API not available' });
        return;
      }
      const res = await api.rejectPromotion(promoId, { notes: comment });
      if (res?.ok !== false) {
        addToast({ tone: 'success', title: 'Promotion rejected' });
        closeSlideout();
        reload();
      } else {
        addToast({ tone: 'danger', title: 'Reject failed', description: res.message || res.error || 'Unknown error' });
      }
      return;
    }
    if (isIntentReviewRow(row)) {
      const versionId = intentVersionIdOf(row);
      if (!versionId) {
        addToast({ tone: 'danger', title: 'Reject failed',
          description: 'Intent version id missing from approval context_data' });
        return;
      }
      if (typeof api.rejectIntentTemplateVersion !== 'function') {
        addToast({ tone: 'danger', title: 'Reject failed', description: 'Intent API not available' });
        return;
      }
      const res = await api.rejectIntentTemplateVersion(versionId, comment || 'Rejected from Approval Center');
      if (res?.ok !== false) {
        addToast({ tone: 'success', title: 'Intent template rejected' });
        closeSlideout();
        reload();
      } else {
        addToast({ tone: 'danger', title: 'Reject failed', description: res.message || res.error || 'Unknown error' });
      }
      return;
    }
    const fn = api.rejectApprovalRequest;
    if (typeof fn !== 'function') {
      addToast({ tone: 'danger', title: 'Reject failed', description: 'API not available' });
      return;
    }
    const res = await fn(id, comment);
    if (res?.ok) {
      addToast({ tone: 'success', title: 'Request rejected' });
      closeSlideout();
      reload();
    } else {
      addToast({ tone: 'danger', title: 'Reject failed', description: res?.error || 'Unknown error' });
    }
  };

  /* Phase 9 — bulk handlers must per-row dispatch promotion vs generic.
   * Mixed bulk batches (some promotions, some skills/workflows) are fine
   * because we route each row to its own endpoint. */
  const handleBulkApprove = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      const row = findRow(id);
      let res;
      if (isPromotionRow(row)) {
        const promoId = promotionEventIdOf(row);
        res = promoId
          ? await api.approvePromotion?.(promoId, { notes: 'bulk approval' })
          : { ok: false, error: 'missing promotion event id' };
        if (res && res.ok !== false) ok++; else fail++;
      } else {
        res = await api.approveApprovalRequest?.(id, 'bulk approval');
        if (res?.ok) ok++; else fail++;
      }
    }
    addToast({
      tone: fail === 0 ? 'success' : 'warning',
      title: `Bulk approve complete`,
      description: `${ok} approved${fail > 0 ? `, ${fail} failed` : ''}`,
    });
    clearSelected();
    reload();
  };

  const handleBulkReject = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      const row = findRow(id);
      let res;
      if (isPromotionRow(row)) {
        const promoId = promotionEventIdOf(row);
        res = promoId
          ? await api.rejectPromotion?.(promoId, { notes: 'bulk rejection' })
          : { ok: false, error: 'missing promotion event id' };
        if (res && res.ok !== false) ok++; else fail++;
      } else {
        res = await api.rejectApprovalRequest?.(id, 'bulk rejection');
        if (res?.ok) ok++; else fail++;
      }
    }
    addToast({
      tone: fail === 0 ? 'success' : 'warning',
      title: `Bulk reject complete`,
      description: `${ok} rejected${fail > 0 ? `, ${fail} failed` : ''}`,
    });
    clearSelected();
    reload();
  };

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      {/* ─── Header ─────────────────────────────────────────────────── */}
      <header className="flex items-start gap-4 mb-5">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Approval Center</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            {allRows.length} request{allRows.length === 1 ? '' : 's'} ·
            {' '}{filtered.length} match current filters
            {' · '}
            {/* PROVE7-037 — derive the header "pending" from the SAME facetCounts the STATUS
                facet renders, so the headline and the facet can never disagree (was a separate
                approvalStats.pending fetch → "2 pending" vs facet "Pending 1" under My Inbox). */}
            <span className="text-v2-warning font-medium">{facetCounts.status?.pending || 0} pending</span>
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>
            Refresh
          </Button>
          <Button variant="secondary" size="sm" onClick={cycleDensity} title={`Row density: ${density}. Click to cycle.`}>
            Density: {density.charAt(0).toUpperCase() + density.slice(1)}
          </Button>
        </div>
      </header>

      {/* ─── Active filter chips (conditional) ──────────────────────── */}
      {activeFilterChips.length > 0 && (
        <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-md shadow-v2-sm px-4 py-2.5 mb-4 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider text-v2-ink-muted font-semibold">Filters</span>
          {activeFilterChips.map((c) => (
            <button
              key={`${c.groupKey}:${c.optionKey}`}
              onClick={() => toggleFacet(c.groupKey, c.optionKey, false)}
              className="inline-flex items-center gap-1 text-[11.5px] font-medium bg-v2-action-soft text-v2-action-strong rounded-v2-pill px-2.5 py-0.5 hover:bg-v2-action hover:text-white transition-colors duration-v2"
            >
              {c.label} ×
            </button>
          ))}
          <button
            onClick={clearFacets}
            className="ml-auto v2-focus-ring text-[11.5px] text-v2-ink-muted hover:text-v2-ink"
          >
            Clear all
          </button>
        </div>
      )}

      {/* ─── Bulk action toolbar (conditional) ──────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="bg-v2-action-soft border border-v2-action rounded-v2-md px-4 py-2.5 mb-4 flex items-center gap-3">
          <span className="text-[12.5px] font-semibold text-v2-action-strong">
            {selectedIds.size} selected
          </span>
          <Button variant="primary" size="sm" iconLeft={<Check size={13} />} onClick={handleBulkApprove}>
            Approve {selectedIds.size}
          </Button>
          <Button variant="danger" size="sm" iconLeft={<X size={13} />} onClick={handleBulkReject}>
            Reject {selectedIds.size}
          </Button>
          <button onClick={clearSelected} className="ml-auto text-[11.5px] text-v2-ink-muted hover:text-v2-ink">
            Clear selection
          </button>
        </div>
      )}

      {/* ─── Main grid: FacetRail + Table ───────────────────────────── */}
      <div className="grid gap-5 grid-cols-1 lg:grid-cols-[240px_1fr]">
        <FacetRail
          groups={facetGroupsWithCounts}
          onToggle={toggleFacet}
          ariaLabel="Approval filters"
        />

        {filtered.length === 0 ? (
          <EmptyState
            icon={<Inbox size={20} />}
            title="No approval requests match"
            description={
              activeFilterChips.length > 0
                ? 'No requests match the current filters. Adjust filters or clear them to see more.'
                : 'You have no approval requests right now. New requests will appear here as agents request human review.'
            }
            primaryAction={
              activeFilterChips.length > 0
                ? <Button variant="primary" onClick={clearFacets}>Clear filters</Button>
                : null
            }
          />
        ) : (
          <Table
            ariaLabel="Approval requests"
            density={density}
            columns={[
              {
                key: 'select', header: '', render: (r) => (
                  <input
                    type="checkbox"
                    checked={selectedIds.has(r.id)}
                    onChange={(e) => { e.stopPropagation(); toggleSelected(r.id); }}
                    onClick={(e) => e.stopPropagation()}
                    className="v2-focus-ring"
                    aria-label={`Select ${r.title || r.id}`}
                  />
                ),
              },
              {
                key: 'urgency', header: '!', render: (r) => urgencyDot(r.urgency),
              },
              {
                key: 'title', header: 'Request', sortable: true, render: (r) => (
                  <div>
                    <div className="font-semibold text-v2-ink">{r.title || r.requestType || 'Untitled'}</div>
                    {/* Flat `sourceName` / server-resolved `requestedByName` — the nested
                        `source.name` / `requestedBy.firstName` shapes the API never returned are
                        kept only as fallbacks. This subtitle rendered "—" on every row. */}
                    <div className="text-[10.5px] text-v2-ink-muted">
                      {r.sourceName || r.source?.name || r.sourceAgent || '—'}
                      {(r.requestedByName
                        || (r.requestedBy && `${r.requestedBy.firstName || ''} ${r.requestedBy.lastName || ''}`.trim()))
                        && ` · ${r.requestedByName
                              || `${r.requestedBy?.firstName || ''} ${r.requestedBy?.lastName || ''}`.trim()}`}
                    </div>
                  </div>
                ),
              },
              {
                key: 'type', header: 'Type', render: (r) => (
                  <Pill tone="muted" size="sm">
                    {(typeOf(r) || 'unknown').replace(/_/g, ' ')}
                  </Pill>
                ),
              },
              {
                key: 'scope', header: 'Scope', render: (r) => {
                  const meta = { inbox: ['cyan', 'My inbox'], myrole: ['warning', 'My role queue'], submitted: ['trust', 'Submitted'], all: ['muted', 'All'] };
                  const scopes = r._scopes ? Array.from(r._scopes) : [];
                  // Hide the catch-all 'all' pill when a more specific scope is present.
                  const show = scopes.filter((sc) => sc !== 'all' || scopes.length === 1);
                  return (
                    <div className="flex flex-wrap gap-1">
                      {show.map((sc) => (
                        <Pill key={sc} tone={(meta[sc] || ['muted', sc])[0]} size="sm">{(meta[sc] || ['', sc])[1]}</Pill>
                      ))}
                    </div>
                  );
                },
              },
              {
                key: 'status', header: 'Status', render: (r) => (
                  <div className="flex flex-col gap-0.5">
                    <Pill tone={STATUS_TONE[r.status] || 'muted'} size="sm" dot>
                      {(r.status || 'unknown').replace(/_/g, ' ')}
                    </Pill>
                    {/* Step 9 (Platform-Eng workflow) — requester-side
                      * visibility for dual-approval. When the operator
                      * filed this request and it now sits at
                      * awaiting_second, surface that prominently on the
                      * row itself so the SRE doesn't have to open the
                      * detail panel to learn their request is moving
                      * through a 2nd approver. */}
                    {r._scopes?.has('submitted') && r.status === 'awaiting_second' && (
                      <span className="text-[10px] text-v2-warning-strong italic">
                        awaiting 2nd approver
                      </span>
                    )}
                  </div>
                ),
              },
              {
                key: 'when', header: 'Requested', align: 'right', render: (r) => (
                  <span className="font-mono-v2 text-[11px] text-v2-ink-muted" title={r.requestedAt}>
                    {fmtRelative(r.requestedAt)}
                  </span>
                ),
              },
              {
                key: 'actions', header: '', align: 'right', render: (r) => (
                  <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    {r._scopes?.has('inbox') && r.status === 'pending' && (
                      <>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleApprove(r.id, '')}
                          className="!text-v2-success hover:!bg-v2-success-soft"
                          title="Approve"
                        >
                          <Check size={13} />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleReject(r.id, '')}
                          className="!text-v2-danger hover:!bg-v2-danger-soft"
                          title="Reject"
                        >
                          <X size={13} />
                        </Button>
                      </>
                    )}
                  </div>
                ),
              },
            ]}
            rows={visibleRows}
            rowKey={(r) => r.id}
            onRowClick={openSlideout}
          />
        )}
      </div>

      {filtered.length > 0 && (
        <div className="grid gap-5 mt-3 grid-cols-1 lg:grid-cols-[240px_1fr]">
          <div />
          <Pagination
            totalItems={filtered.length}
            page={pg.page}
            pageSize={pg.pageSize}
            onPageChange={pg.setPage}
            onPageSizeChange={pg.setPageSize}
            noun="approvals"
          />
        </div>
      )}

      {/* ─── Slideout — request triage view ─────────────────────────── */}
      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.title || selected.id} detail` : 'Approval request detail'}
        width={520}
        tabs={[
          { id: 'summary',  label: 'Summary' },
          { id: 'history',  label: 'Decision history' },
          { id: 'audit',    label: 'Audit trail' },
          { id: 'policy',   label: 'Policy' },
          { id: 'comments', label: 'Comments' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={URGENCY_TONE[selected.urgency] || 'muted'} size="sm" dot>
                {selected.urgency || 'unknown'}
              </Pill>
              <Pill tone={STATUS_TONE[selected.status] || 'muted'} size="sm" dot>
                {(selected.status || 'unknown').replace(/_/g, ' ')}
              </Pill>
              <Pill tone="muted" size="sm">
                {(typeOf(selected) || 'unknown').replace(/_/g, ' ')}
              </Pill>
            </div>
            <h2 className="text-[18px] font-bold tracking-tight m-0 leading-snug">
              {selected.title || selected.requestType || 'Approval request'}
            </h2>
            <p className="text-[12px] text-v2-ink-secondary mt-1">
              {/* Same nested-shape bug as the Source field: the API serialises `sourceName` flat,
                  so this read `source.name` and printed "Unknown source" above a request that
                  plainly knew its source. */}
              {selected.sourceName || selected.source?.name || selected.sourceAgent || 'Unknown source'}
              {selected.requestedAt && ` · requested ${fmtRelative(selected.requestedAt)}`}
            </p>

            {/* Header action row — primary decision controls
             *
             * RC-103a — Approve now passes formData when the request carries
             * a formSchema. validateFormData() short-circuits on missing
             * required fields and sets formError (rendered next to the
             * form block below). Reject ignores formData by design —
             * rejection doesn't require the operator to fill the form.
             *
             * ZERO REGRESSION: when hasFormSchema is false, formData is
             * {} and is passed to handleApprove as `undefined` (api.js
             * line 1212-1214 already omits formData from the wire body
             * when undefined). Existing approve-without-form path is
             * bit-for-bit unchanged. */}
            {selected._scopes?.has('inbox') && selected.status === 'pending' && (
              <div className="flex items-center gap-2 mt-3">
                <Button
                  variant="primary"
                  size="sm"
                  iconLeft={<Check size={13} />}
                  onClick={() => {
                    if (!validateFormData()) return;
                    const payload = hasFormSchema ? formData : undefined;
                    handleApprove(selected.id, '', payload);
                  }}
                >
                  Approve
                </Button>
                <Button variant="danger" size="sm" iconLeft={<X size={13} />} onClick={() => handleReject(selected.id, '')}>
                  Reject
                </Button>
              </div>
            )}
            {selected._scopes?.has('submitted') && selected.status === 'pending' && (
              <div className="flex items-center gap-2 mt-3">
                <Button variant="ghost" size="sm" onClick={async () => {
                  const fn = api.cancelApprovalRequest;
                  if (typeof fn !== 'function') return;
                  const res = await fn(selected.id);
                  if (res?.ok) { addToast({ tone: 'info', title: 'Request cancelled' }); closeSlideout(); reload(); }
                }}>
                  Cancel request
                </Button>
              </div>
            )}
          </div>
        )}
      >
        {selected && slideoutTab === 'summary' && (
          <div className="space-y-3 text-[12.5px] text-v2-ink-secondary leading-relaxed">

            {/* RC-103a — FORM-INPUT CARD (form_task / human_task with formSchema)
             *
             * Renders ONLY when the selected request carries a non-empty
             * formSchema AND it's pending in the operator's inbox. The card
             * is the first child so operators see the form before scrolling
             * past metadata. Approve button at the top is wired to validate
             * + submit formData (see header action row above).
             *
             * Field types supported:
             *   text     → <Input>
             *   textarea → <Textarea> (8 rows)
             *   select   → <Select> with optional options array
             *   number   → <Input type=number>
             *   boolean  → <input type=checkbox> wrapped in a label (V2
             *              primitives don't have a Checkbox component yet;
             *              raw <input> for this ONE field type is the
             *              pragmatic exception)
             *
             * SAFETY: when hasFormSchema is false, this entire block
             * renders nothing — Cards below render as before.
             */}
            {hasFormSchema
              && selected._scopes?.has('inbox')
              && selected.status === 'pending' && (
              <Card padding="sm">
                <Card.Header
                  title="Form (required for approval)"
                  subtitle="Submitted values are persisted on the audit trail and forwarded to downstream nodes as ${nodeId}.<fieldName>."
                />
                <Card.Body>
                  <div className="space-y-3">
                    {selectedFormSchema.fields.map((field) => {
                      if (!field || !field.name) return null;
                      /* RC-121 — show-if filter. Hidden fields skip both
                       * render AND required-check (in validateFormData),
                       * so an operator can't be blocked on a field they
                       * can't see. Authors model dependent fields by
                       * adding `showIf: { field, equals|in|notIn|... }`
                       * to the dependent field. */
                      if (!evaluateShowIf(field, formData)) return null;
                      const val = formData[field.name];
                      const setVal = (v) => {
                        setFormData((prev) => ({ ...prev, [field.name]: v }));
                        if (formError) setFormError(null);
                      };
                      const ft = (field.type || 'text').toLowerCase();
                      const label = field.label || field.name;
                      const required = !!field.required;

                      if (ft === 'textarea') {
                        return (
                          <FieldGroup key={field.name} label={label} required={required} hint={field.helpText}>
                            <Textarea
                              rows={6}
                              value={val ?? ''}
                              onChange={(e) => setVal(e.target.value)}
                              placeholder={field.placeholder || ''}
                            />
                          </FieldGroup>
                        );
                      }
                      if (ft === 'select') {
                        const opts = Array.isArray(field.options) ? field.options : [];
                        return (
                          <FieldGroup key={field.name} label={label} required={required} hint={field.helpText}>
                            <Select
                              value={val ?? ''}
                              onChange={(e) => setVal(e.target.value)}
                            >
                              <option value="">Select…</option>
                              {opts.map((opt, i) => {
                                // Tolerate both ["a","b"] and [{value,label}, ...] shapes
                                const v = typeof opt === 'object' && opt !== null ? opt.value : opt;
                                const l = typeof opt === 'object' && opt !== null ? (opt.label || opt.value) : opt;
                                return <option key={`${v}-${i}`} value={v}>{l}</option>;
                              })}
                            </Select>
                          </FieldGroup>
                        );
                      }
                      if (ft === 'number') {
                        return (
                          <FieldGroup key={field.name} label={label} required={required} hint={field.helpText}>
                            <Input
                              type="number"
                              value={val ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value;
                                // Empty string stays empty (operator may delete);
                                // otherwise coerce to number so BE receives the right type.
                                setVal(raw === '' ? '' : Number(raw));
                              }}
                              placeholder={field.placeholder || ''}
                            />
                          </FieldGroup>
                        );
                      }
                      /* RC-121 — date / datetime / multi-select / multiline-date branches.
                       *
                       * `date` and `datetime` map to native HTML5
                       * controls — operators get the OS date-picker for
                       * free and the value is an ISO string the BE
                       * stores verbatim (date: 'YYYY-MM-DD',
                       * datetime: 'YYYY-MM-DDTHH:mm' — the
                       * `datetime-local` standard format).
                       *
                       * `multi-select` (also `multiselect`) renders as
                       * an accessible checkbox group rather than
                       * `<select multiple>` because the native
                       * multi-select is hard to discover on touch /
                       * non-power users (cmd/ctrl-click to add) and
                       * doesn't match the rest of the V2 form
                       * primitives' look-and-feel. Value is an array of
                       * the checked option values; downstream nodes
                       * receive it as `${nodeId}.fieldName` and can
                       * `contains` / `length` it from jq.
                       *
                       * RC-121b shipped the file-upload branch below
                       * (before the boolean branch). Value shape:
                       *   { storageUrl, attachmentId, filename, sizeBytes,
                       *     sha256, contentType }
                       * — written by the upload handler on file-select.
                       */
                      if (ft === 'date') {
                        return (
                          <FieldGroup key={field.name} label={label} required={required} hint={field.helpText}>
                            <Input
                              type="date"
                              value={val ?? ''}
                              onChange={(e) => setVal(e.target.value)}
                              min={field.min || undefined}
                              max={field.max || undefined}
                            />
                          </FieldGroup>
                        );
                      }
                      if (ft === 'datetime' || ft === 'datetime-local') {
                        return (
                          <FieldGroup key={field.name} label={label} required={required} hint={field.helpText}>
                            <Input
                              type="datetime-local"
                              value={val ?? ''}
                              onChange={(e) => setVal(e.target.value)}
                              min={field.min || undefined}
                              max={field.max || undefined}
                            />
                          </FieldGroup>
                        );
                      }
                      if (ft === 'multi-select' || ft === 'multiselect') {
                        const opts = Array.isArray(field.options) ? field.options : [];
                        const selectedSet = new Set(Array.isArray(val) ? val : []);
                        return (
                          <FieldGroup key={field.name} label={label} required={required} hint={field.helpText}>
                            <div className="space-y-1.5">
                              {opts.length === 0 && (
                                <div className="text-[11px] text-v2-ink-muted italic">No options configured.</div>
                              )}
                              {opts.map((opt, i) => {
                                const v = typeof opt === 'object' && opt !== null ? opt.value : opt;
                                const l = typeof opt === 'object' && opt !== null ? (opt.label || opt.value) : opt;
                                const checked = selectedSet.has(v);
                                return (
                                  <label
                                    key={`${v}-${i}`}
                                    className="flex items-center gap-2 text-[12.5px] cursor-pointer select-none"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={(e) => {
                                        const next = new Set(selectedSet);
                                        if (e.target.checked) next.add(v); else next.delete(v);
                                        // Preserve original opts order in the array — keeps
                                        // BE side deterministic regardless of click order.
                                        setVal(opts
                                          .map((o) => (typeof o === 'object' && o !== null ? o.value : o))
                                          .filter((x) => next.has(x)));
                                      }}
                                      className="h-4 w-4"
                                    />
                                    <span>{l}</span>
                                  </label>
                                );
                              })}
                            </div>
                          </FieldGroup>
                        );
                      }
                      /* RC-121b — file upload. The BE endpoint
                       *   POST /api/v1/approvals/{id}/attachments
                       * persists the bytes (UploadGuard validates magic
                       * bytes + size + sanitizes the filename) and
                       * returns a storageUrl the FE stores as the field
                       * value. Downstream workflow nodes consume the URL
                       * via the auth-gated GET companion endpoint.
                       *
                       * Value shape: either null (no file) or an object
                       * { storageUrl, filename, sizeBytes, sha256,
                       *   contentType, attachmentId } — mirrors the BE
                       * upload response. Downstream consumers can read
                       * `.storageUrl` directly via jq:
                       *   .formData.<fieldName>.storageUrl
                       *
                       * Replace-on-reselect semantics: picking a new
                       * file replaces the previous attachment in
                       * form_data; the old DB row is orphaned (acceptable
                       * tradeoff — RC-121c may add a delete-prior-row
                       * pass if storage usage becomes a concern). */
                      if (ft === 'file') {
                        const attached = val && typeof val === 'object' && val.storageUrl
                          ? val
                          : null;
                        const onPickFile = async (e) => {
                          const f = e.target.files && e.target.files[0];
                          // Reset the input so re-picking the same file fires onChange again.
                          if (e.target) e.target.value = '';
                          if (!f) return;
                          try {
                            const resp = await api.uploadApprovalAttachment(
                              selected.id,
                              f,
                              field.name,
                            );
                            // resp shape:
                            //   { id, storageUrl, sha256, sizeBytes, filename, contentType, fieldKey }
                            setVal({
                              storageUrl:   resp.storageUrl,
                              attachmentId: resp.id,
                              filename:     resp.filename,
                              sizeBytes:    resp.sizeBytes,
                              sha256:       resp.sha256,
                              contentType:  resp.contentType,
                            });
                          } catch (err) {
                            // Surface the structured reason where we have one
                            // (e.g. "content-extension-mismatch" → operator-friendly).
                            const detail = err && err.reason
                              ? `${err.message} (${err.reason})`
                              : (err && err.message) || 'Upload failed';
                            setFormError(`Could not attach "${f.name}": ${detail}`);
                          }
                        };
                        const fmtBytes = (n) => {
                          if (n == null) return '';
                          if (n < 1024) return `${n} B`;
                          if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
                          return `${(n / (1024 * 1024)).toFixed(1)} MB`;
                        };
                        return (
                          <FieldGroup key={field.name} label={label} required={required} hint={field.helpText}>
                            {attached ? (
                              <div className="flex items-center gap-3 px-3 py-2 rounded border border-v2-line-soft bg-v2-bg-elevated">
                                <a
                                  href={api.buildApprovalAttachmentDownloadUrl(selected.id, attached.attachmentId)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-[12.5px] underline text-v2-ink truncate"
                                  title={attached.filename}
                                >
                                  {attached.filename}
                                </a>
                                <span className="text-[11px] text-v2-ink-muted whitespace-nowrap">
                                  {fmtBytes(attached.sizeBytes)}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setVal(null)}
                                  className="ml-auto text-[11px] text-v2-danger hover:underline"
                                >
                                  Remove
                                </button>
                              </div>
                            ) : (
                              <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded border border-v2-line-soft bg-v2-bg-elevated cursor-pointer text-[12.5px] hover:bg-v2-bg-muted">
                                <input
                                  type="file"
                                  accept={field.accept || undefined}
                                  className="hidden"
                                  onChange={onPickFile}
                                />
                                Choose a file…
                              </label>
                            )}
                          </FieldGroup>
                        );
                      }
                      if (ft === 'boolean') {
                        return (
                          <label
                            key={field.name}
                            className="flex items-center gap-2 text-[12.5px] cursor-pointer select-none"
                          >
                            <input
                              type="checkbox"
                              checked={!!val}
                              onChange={(e) => setVal(e.target.checked)}
                              className="h-4 w-4"
                            />
                            <span>
                              {label}
                              {required && <span className="text-v2-danger ml-1">*</span>}
                            </span>
                          </label>
                        );
                      }
                      // Default: text input
                      return (
                        <FieldGroup key={field.name} label={label} required={required} hint={field.helpText}>
                          <Input
                            value={val ?? ''}
                            onChange={(e) => setVal(e.target.value)}
                            placeholder={field.placeholder || ''}
                          />
                        </FieldGroup>
                      );
                    })}

                    {formError && (
                      <div className="text-[12px] text-v2-danger px-1 py-2 rounded bg-v2-danger-bg-subtle border border-v2-danger-border">
                        {formError}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* RC-152b — Post-decision form-value visibility.
              *
              * Today's editable-form block above ONLY renders when
              * `_scopes.has('inbox') && status === 'pending'` — correct
              * for the input flow, but it also hides the form completely
              * once the decision has been made OR for any non-inbox
              * viewer (admin All-Requests scope, audit reviewers,
              * Submitted scope). The submitted answers stay in the DB
              * (approval_requests.form_data) but never reach the screen.
              *
              * Result before this fix: a security reviewer asking
              * "what did approver X actually approve on this row?" sees
              * only firstDecidedBy + firstDecidedAt + comment — never
              * the actual form values. Same gap for an operator
              * checking their own decided rows in Submitted scope.
              *
              * This block fires when the form schema exists AND the
              * editable block above does NOT (the conditions are
              * mutually exclusive). Renders a read-only one-row-per-
              * field summary using the SAME field shape as the schema
              * — labels, types, and submitted values. File fields
              * display the descriptive stub RC-149 substituted at the
              * gate's flatten site, so the row reads as
              * "[FILE: claim_document.pdf, 2547000b, sha256=a1b2c3d4]"
              * — useful audit signal, never the raw bytes/URL. Fields
              * the operator left unfilled (legitimate for non-required
              * fields, or fields hidden by showIf at the time) display
              * as em-dash so the reviewer can distinguish "answered
              * with empty" from "wasn't asked."
              *
              * ZERO REGRESSION: condition is `!(inbox+pending)` so
              * the editable block continues to own the active-input
              * state; this block only activates for the surfaces that
              * previously rendered nothing form-related. */}
            {hasFormSchema
              && !(selected._scopes?.has('inbox') && selected.status === 'pending') && (
              <Card padding="sm">
                <Card.Header
                  title="Form values submitted"
                  subtitle="Read-only audit view of the answers provided to this approval. File fields show the descriptive reference; the original upload stays in approval attachments under tenant scope."
                />
                <Card.Body>
                  <dl className="grid grid-cols-1 gap-3 text-[12px]">
                    {selectedFormSchema.fields.map((field) => {
                      if (!field || !field.name) return null;
                      const submitted = selected.formData
                        ? selected.formData[field.name] : undefined;
                      const hasValue = submitted !== undefined
                        && submitted !== null
                        && !(typeof submitted === 'string' && submitted.trim().length === 0)
                        && !(Array.isArray(submitted) && submitted.length === 0);
                      const ft = (field.type || 'text').toLowerCase();
                      const isFile = ft === 'file';
                      const label = field.label || field.name;

                      // Multi-select renders as a comma-joined list;
                      // checkboxes render as Yes/No; other types print
                      // the value with whitespace preserved for textareas.
                      let displayValue;
                      if (!hasValue) {
                        displayValue = (<span className="text-v2-ink-muted italic">—</span>);
                      } else if (ft === 'multi-select' || ft === 'multiselect') {
                        const arr = Array.isArray(submitted) ? submitted : [submitted];
                        displayValue = arr.join(', ');
                      } else if (ft === 'boolean' || ft === 'checkbox') {
                        displayValue = submitted ? 'Yes' : 'No';
                      } else if (ft === 'textarea') {
                        displayValue = (
                          <span className="whitespace-pre-wrap">{String(submitted)}</span>
                        );
                      } else if (isFile) {
                        // RC-149 redaction stub format:
                        //   [FILE: name, sizeB, sha256=short]
                        // Render as a monospace code-style chip so the
                        // sha256 / size metadata reads cleanly, and so
                        // the reviewer can copy the sha256 prefix into
                        // forensic tools without selecting around the
                        // prose label.
                        displayValue = (
                          <code className="px-1.5 py-0.5 rounded bg-v2-bg-elevated text-v2-ink text-[11px] font-mono break-all">
                            {String(submitted)}
                          </code>
                        );
                      } else {
                        displayValue = String(submitted);
                      }

                      return (
                        <div key={field.name} className="flex flex-col gap-0.5">
                          <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold flex items-center gap-2">
                            <span>{label}</span>
                            <span className="text-[9.5px] text-v2-ink-muted font-normal normal-case tracking-normal">
                              {ft}{field.required ? ' · required' : ''}
                            </span>
                          </dt>
                          <dd className="text-v2-ink mt-0.5">
                            {displayValue}
                          </dd>
                        </div>
                      );
                    })}
                    {!selected.formData && (
                      <div className="text-v2-ink-muted italic text-[11px]">
                        No form values were submitted with this decision.
                      </div>
                    )}
                  </dl>
                </Card.Body>
              </Card>
            )}

            <Card padding="sm">
              <Card.Header title="Request detail" />
              <Card.Body>
                <dl className="grid grid-cols-2 gap-3 text-[12px]">
                  <div>
                    <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Type</dt>
                    <dd className="text-v2-ink mt-0.5">{(typeOf(selected) || 'unknown').replace(/_/g, ' ')}</dd>
                  </div>
                  <div>
                    <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Urgency</dt>
                    <dd className="text-v2-ink mt-0.5">{selected.urgency || 'unknown'}</dd>
                  </div>
                  <div>
                    <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Source</dt>
                    {/* The API serialises the entity flat (`sourceName`); `source.name` was a nested
                        shape it has never returned, so this rendered "—" on every approval. */}
                    <dd className="text-v2-ink mt-0.5">{selected.sourceName || selected.source?.name || selected.sourceAgent || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Requested</dt>
                    <dd className="text-v2-ink mt-0.5">{selected.requestedAt ? new Date(selected.requestedAt).toLocaleString() : '—'}</dd>
                  </div>
                  {/* WHO OWES THE DECISION. This was missing entirely — the queue could tell you an
                      approval existed but not who it had been routed to, which makes "escalated"
                      an unanswerable claim. Name resolved server-side (R2). */}
                  <div className="col-span-2">
                    <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Assigned to</dt>
                    <dd className="text-v2-ink mt-0.5">
                      {/* PROVE7-038 — a ROLE-QUEUE assignment (assignedRole set, no specific
                          assignee) is NOT unresolved: it is actionable by any holder of that role
                          via the role-aware inbox. Only a request with neither a user nor a role
                          is genuinely unresolved. */}
                      {selected.assignedToName
                        ? selected.assignedToName
                        : selected.assignedTo
                          ? 'Assigned'
                          : selected.assignedRole
                            ? `Role queue — any ${selected.assignedRole} approver`
                            : 'Unassigned — no approver could be resolved'}
                      {selected.assignedRole && selected.assignedTo && (
                        <span className="text-v2-ink-muted"> · {selected.assignedRole}</span>
                      )}
                    </dd>
                  </div>
                  {(selected.requestedByName || selected.requestedBy) && (
                    <div className="col-span-2">
                      <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Requested by</dt>
                      <dd className="text-v2-ink mt-0.5">
                        {selected.requestedByName
                          || `${selected.requestedBy?.firstName || ''} ${selected.requestedBy?.lastName || ''}`.trim()
                          || '—'}
                        {selected.requestedBy?.email && <span className="text-v2-ink-muted"> · {selected.requestedBy.email}</span>}
                      </dd>
                    </div>
                  )}
                  {selected.slaDeadline && (
                    <div className="col-span-2">
                      <dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">SLA</dt>
                      <dd className="text-v2-ink mt-0.5">{new Date(selected.slaDeadline).toLocaleString()}</dd>
                    </div>
                  )}
                </dl>
              </Card.Body>
            </Card>

            {/* Step 9 (Platform-Eng workflow) — dual-approval state
              * visibility. Renders when the request has a second
              * approver assigned OR is currently awaiting one. Closes
              * the requester-side gap from the platform-engineer
              * walkthrough: "your request is awaiting second approval
              * from N peers" needs to be a UI artifact, not a SQL
              * lookup. Shows side-by-side first + second approver
              * state with decision timestamps + comments. */}
            {/* PROVE7-041 — render the Dual-approval card ONLY for an ACTUAL dual approval.
                The old `|| selected.firstDecision != null` also fired for SINGLE approvals, because
                handleFinalApproval (single) sets firstDecision='approved' — so a fully-resolved
                single approval showed a phantom "needs 2nd approval to fully resolve". Gate on real
                second-approver signals: approvalType=dual, a second assignee/role, awaiting_second,
                or a recorded second decision. */}
            {(selected.approvalType === 'dual'
                || selected.secondAssignedTo
                || selected.secondAssignedRole
                || selected.status === 'awaiting_second'
                || selected.secondDecision != null) && (
              <Card padding="sm">
                <Card.Header
                  title="Dual approval"
                  actions={
                    selected.status === 'awaiting_second' ? (
                      <Pill tone="warning" size="sm" dot>
                        awaiting 2nd
                      </Pill>
                    ) : (selected.firstDecision === 'approved'
                          && selected.secondDecision === 'approved') ? (
                      /* E2E cosmetic fix — the previous condition
                       * was `status === 'approved'`, which is true
                       * for single-approval flows too. The Card
                       * header should only claim 'both approved'
                       * when BOTH decisions are actually approved
                       * (otherwise the second-approver column
                       * would still show 'pending' below). */
                      <Pill tone="success" size="sm" dot>
                        both approved
                      </Pill>
                    ) : (selected.firstDecision === 'rejected'
                          || selected.secondDecision === 'rejected') ? (
                      <Pill tone="danger" size="sm" dot>
                        rejected
                      </Pill>
                    ) : null
                  }
                />
                <Card.Body>
                  <div className="grid grid-cols-2 gap-3 text-[12px]">
                    {/* First approver column */}
                    <div className="border border-v2-border-subtle rounded-v2-sm p-2.5">
                      <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">
                        First approver
                      </div>
                      <div className="text-v2-ink">
                        {selected.assignedTo
                          ? <code className="font-mono-v2 text-[11px]">{String(selected.assignedTo).slice(0, 8)}…</code>
                          : (selected.assignedRole || '—')}
                      </div>
                      {selected.firstDecision ? (
                        <div className="mt-1">
                          <Pill
                            tone={selected.firstDecision === 'approved' ? 'success'
                                  : selected.firstDecision === 'rejected' ? 'danger' : 'muted'}
                            size="sm" dot>
                            {selected.firstDecision}
                          </Pill>
                          {selected.firstDecidedAt && (
                            <span className="ml-1.5 text-[10.5px] text-v2-ink-muted">
                              {fmtRelative(selected.firstDecidedAt)}
                            </span>
                          )}
                          {selected.firstComment && (
                            <p className="mt-1 text-[11px] text-v2-ink-secondary italic m-0">
                              "{selected.firstComment}"
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="mt-1 text-[10.5px] text-v2-ink-muted italic">
                          pending decision
                        </div>
                      )}
                    </div>

                    {/* Second approver column */}
                    <div className="border border-v2-border-subtle rounded-v2-sm p-2.5">
                      <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">
                        Second approver
                      </div>
                      <div className="text-v2-ink">
                        {selected.secondAssignedTo
                          ? <code className="font-mono-v2 text-[11px]">{String(selected.secondAssignedTo).slice(0, 8)}…</code>
                          : (selected.secondAssignedRole || (selected.status === 'awaiting_second' ? 'awaiting assignment' : '—'))}
                      </div>
                      {selected.secondDecision ? (
                        <div className="mt-1">
                          <Pill
                            tone={selected.secondDecision === 'approved' ? 'success'
                                  : selected.secondDecision === 'rejected' ? 'danger' : 'muted'}
                            size="sm" dot>
                            {selected.secondDecision}
                          </Pill>
                          {selected.secondDecidedAt && (
                            <span className="ml-1.5 text-[10.5px] text-v2-ink-muted">
                              {fmtRelative(selected.secondDecidedAt)}
                            </span>
                          )}
                          {selected.secondComment && (
                            <p className="mt-1 text-[11px] text-v2-ink-secondary italic m-0">
                              "{selected.secondComment}"
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="mt-1 text-[10.5px] text-v2-warning-strong italic">
                          {selected.status === 'awaiting_second'
                            ? 'awaiting decision — routed for 2nd approval'
                            : selected.firstDecision === 'approved'
                              ? 'pending — needs 2nd approval to fully resolve'
                              : '—'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Requester-side advisory — explicit text the SRE
                    * sees on their own Submitted-scope view. */}
                  {selected._scopes?.has('submitted') && selected.status === 'awaiting_second' && (
                    <p className="mt-2.5 text-[11.5px] text-v2-ink leading-relaxed m-0">
                      Your request was approved by the first reviewer and is now
                      awaiting a second approver before it can be enacted.
                      You'll get a notification when the second decision lands.
                    </p>
                  )}
                </Card.Body>
              </Card>
            )}

            {selected.description && (
              <Card padding="sm">
                <Card.Header title="Description" />
                <Card.Body>
                  <p className="text-[12.5px] text-v2-ink leading-relaxed whitespace-pre-wrap">{selected.description}</p>
                </Card.Body>
              </Card>
            )}

            {/* WHAT IS BEING APPROVED.
                The Approval Center never rendered `contextData` — for any approval type. So an
                approver was asked to authorise an action without being shown the action. For a
                human-gated agent tool call that is disqualifying: nobody can sanction quarantining
                a production endpoint without being told WHICH endpoint. The request description
                even said "review the requested arguments below" — and there was nothing below.
                Rendered read-only; the JSON is the agent's own tool-call arguments, verbatim. */}
            {selected.contextData && (
              <Card>
                <Card.Header title="Requested action" />
                <Card.Body>
                  {selected.contextData.requestedArgs && (
                    <p className="text-[11.5px] text-v2-ink-muted mb-2">
                      These are the exact arguments the agent asked to run with. The action has not been performed.
                    </p>
                  )}
                  <pre className="text-[11.5px] leading-relaxed text-v2-ink bg-v2-surface-sunken border border-v2-border rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-words">
                    {JSON.stringify(selected.contextData, null, 2)}
                  </pre>
                </Card.Body>
              </Card>
            )}
          </div>
        )}

        {selected && slideoutTab === 'history' && (
          <Card padding="sm">
            <Card.Header title="Decision history" />
            <Card.Body>
              {Array.isArray(selected.decisionHistory) && selected.decisionHistory.length > 0 ? (
                <ul className="space-y-2 text-[12.5px] text-v2-ink-secondary leading-relaxed">
                  {selected.decisionHistory.map((d, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">{d.timestamp ? fmtRelative(d.timestamp) : '—'}</span>
                      <span><strong className="text-v2-ink">{d.actor || 'system'}</strong> {d.action || '—'}{d.comment && <span className="text-v2-ink-muted"> · {d.comment}</span>}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-v2-ink-muted text-[12.5px]">No decisions recorded yet.</p>
              )}
            </Card.Body>
          </Card>
        )}

        {selected && slideoutTab === 'audit' && (
          <div className="space-y-3">
            <Card padding="sm">
              <Card.Header title="Audit trail (recent)" />
              <Card.Body>
                <ul className="space-y-2 text-[12.5px] text-v2-ink-secondary leading-relaxed">
                  <li className="flex items-baseline gap-2">
                    <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">{selected.requestedAt ? fmtRelative(selected.requestedAt) : '—'}</span>
                    <span>Request opened</span>
                  </li>
                  {selected.firstApprovedAt && (
                    <li className="flex items-baseline gap-2">
                      <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">{fmtRelative(selected.firstApprovedAt)}</span>
                      <span>First approval recorded</span>
                    </li>
                  )}
                  {selected.escalatedAt && (
                    <li className="flex items-baseline gap-2">
                      <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">{fmtRelative(selected.escalatedAt)}</span>
                      <span>Escalated</span>
                    </li>
                  )}
                  {selected.completedAt && (
                    <li className="flex items-baseline gap-2">
                      <span className="font-mono-v2 text-[10.5px] text-v2-ink-muted w-32 flex-shrink-0">{fmtRelative(selected.completedAt)}</span>
                      <span>Final decision recorded</span>
                    </li>
                  )}
                </ul>
              </Card.Body>
            </Card>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate(`/workflow-audit?approvalId=${encodeURIComponent(selected.id)}`)} /* G436 */
            >
              Open full audit trail →
            </Button>
          </div>
        )}

        {selected && slideoutTab === 'policy' && (
          <Card padding="sm">
            <Card.Header title="Policy applied" />
            <Card.Body>
              {selected.policyName ? (
                <div className="space-y-2 text-[12.5px]">
                  <div>
                    <span className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Policy</span>
                    <div className="text-v2-ink mt-0.5">{selected.policyName}</div>
                  </div>
                  {selected.dualApprovalRequired && (
                    <div className="px-3 py-2 rounded-v2-sm bg-v2-warning-soft text-v2-warning text-[12px]">
                      <strong>Dual approval required.</strong> A second approver must sign before this request can be acted on.
                    </div>
                  )}
                  <Button variant="ghost" size="sm" onClick={() => navigate('/policy-config')}>
                    Open policy config →
                  </Button>
                </div>
              ) : (
                <p className="text-v2-ink-muted text-[12.5px]">No specific policy applied to this request.</p>
              )}
            </Card.Body>
          </Card>
        )}

        {selected && slideoutTab === 'comments' && (
          <Card padding="sm">
            <Card.Header title="Comments" />
            <Card.Body>
              {Array.isArray(selected.comments) && selected.comments.length > 0 ? (
                <ul className="space-y-3 text-[12.5px]">
                  {selected.comments.map((c, i) => (
                    <li key={i} className="border-l-2 border-v2-border-subtle pl-3">
                      <div className="text-[10.5px] text-v2-ink-muted">
                        <strong className="text-v2-ink">{c.author || 'Anonymous'}</strong> · {c.timestamp ? fmtRelative(c.timestamp) : '—'}
                      </div>
                      <div className="text-v2-ink mt-0.5 whitespace-pre-wrap">{c.body}</div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-v2-ink-muted text-[12.5px]">No comments on this request.</p>
              )}
            </Card.Body>
          </Card>
        )}
      </Slideout>
    </div>
  );
}

/* ── Resolver ─────────────────────────────────────────────────────────
 * Per G88: when v2 isn't a quality bar yet, render v1. Now that v2
 * is a proper inventory pattern (G107), gate behind the rebirth flag
 * and fall back to v1 when off. */
export default function ResolvedApprovalCenterPage(props) {
  if (isRebirthApprovalsEnabled()) return <ApprovalCenterPageV2Inner {...props} />;
  return <ApprovalCenterPage {...props} />;
}

export { ApprovalCenterPageV2Inner };
